// src/pages/HymnPage.tsx
import React, { useCallback, useEffect, useMemo } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import AudioCard from "../../components/cards/AudioCard";
import CustomLoader from "../../components/placeholders/CustomLoader";
import FilterBar from "../../components/cards/FilterBar";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import { useRecordings } from "../../context/RecordingContext";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import EmptyState from "../../components/placeholders/EmptyState";
import { MusicNotesIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";
import { Recording } from "../../model/recording";

const HymnPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    isLoading,
    rubrics,
    hasInitialized,
    setHasInitialized,
    selectedRubricIDs,
    setSelectedRubricIDs,
    recordingsByCategory,
    recordingsByRubric,
    listRubrics,
    listFavoriteIDs,
    listRecordingsByCategory,
    listRecordingsByRubric,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const selectedRubricID = selectedRubricIDs.hymn ?? "";
  const hymnRubrics = useMemo(
    () => rubrics.filter((rubric) => rubric.category === "hymn"),
    [rubrics],
  );
  const hymnRubricIDs = useMemo(
    () => hymnRubrics.map((rubric) => rubric.rubricID),
    [hymnRubrics],
  );
  const {
    searchMode,
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  } = useSharedRecordingQuery({
    matchRecording: (recording: Recording) =>
      recording.category === "hymn" &&
      (!recording.rubricID ||
        hymnRubricIDs.length === 0 ||
        hymnRubricIDs.includes(recording.rubricID)),
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.hymns.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  useEffect(() => {
    if (hasInitialized.hymn) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        // Phase 1: fetch rubrics and favorites in parallel
        const [fetchedRubrics] = await Promise.all([
          listRubrics(),
          listFavoriteIDs(),
        ]);

        // Phase 2: pick the most recent hymn rubric
        const hymnRubrics = fetchedRubrics.filter((r) => r.category === "hymn");
        const mostRecent = hymnRubrics.length > 0 ? hymnRubrics[0] : null;
        const initialRubricID = selectedRubricID || mostRecent?.rubricID || "";

        // Phase 3: fetch recordings exactly once
        if (initialRubricID) {
          setSelectedRubricIDs((prev) => ({ ...prev, hymn: initialRubricID }));
          await listRecordingsByRubric("hymn", initialRubricID);
        } else {
          await listRecordingsByCategory("hymn");
        }
      } catch (error) {
        toast({
          title: t("pages.hymns.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, hymn: true }));
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.hymn,
    listRubrics,
    listFavoriteIDs,
    listRecordingsByRubric,
    listRecordingsByCategory,
    selectedRubricID,
    setHasInitialized,
    setSelectedRubricIDs,
    t,
    toast,
  ]);

  const handleRubricChange = useCallback(
    (rubricID: string) => {
      setSelectedRubricIDs((prev) => ({ ...prev, hymn: rubricID }));
      if (rubricID) {
        listRecordingsByRubric("hymn", rubricID).catch((error) => {
          toast({
            title: t("pages.hymns.filterErrorTitle"),
            description: extractApiErrorMessage(
              error,
              String(t("common.tryAgain")),
            ),
            status: "error",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
        });
      } else {
        listRecordingsByCategory("hymn").catch((error) => {
          toast({
            title: t("pages.hymns.loadErrorTitle"),
            description: extractApiErrorMessage(
              error,
              String(t("common.tryAgain")),
            ),
            status: "error",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
        });
      }
    },
    [
      listRecordingsByCategory,
      listRecordingsByRubric,
      setSelectedRubricIDs,
      t,
      toast,
    ],
  );

  const rubricKey = selectedRubricID ? `hymn:${selectedRubricID}` : "";

  const scopedRecordings = useMemo(
    () =>
      selectedRubricID
        ? (recordingsByRubric[rubricKey] ?? [])
        : (recordingsByCategory.hymn ?? []),
    [
      recordingsByCategory.hymn,
      recordingsByRubric,
      rubricKey,
      selectedRubricID,
    ],
  );

  const displayedRecordings = useMemo(
    () => (sharedRecording ? [sharedRecording] : scopedRecordings),
    [scopedRecordings, sharedRecording],
  );

  const hasNoData =
    !selectedRubricID && (recordingsByCategory.hymn ?? []).length === 0;
  const hasNoResults = displayedRecordings.length === 0 && !hasNoData;

  return (
    <VStack w={DEFAULT_WIDTH} mx="auto" align="start" px={PAGE_PADDING}>
      <FilterBar
        onSearch={setSearchQuery}
        searchValue={searchQuery}
        rubrics={hymnRubrics}
        selectedRubricID={selectedRubricID}
        onRubricChange={handleRubricChange}
        placeholder={String(t("pages.hymns.searchPlaceholder"))}
        searchMode={searchMode}
        onSuggestionSelect={(item) => selectSharedRecording(item.recordingID)}
      />
      {isLoading || isLoadingSharedRecording || !hasInitialized.hymn ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={MusicNotesIcon}
          title={t("pages.hymns.emptyTitle")}
          description={t("pages.hymns.emptyDescription")}
        />
      ) : hasNoResults ? (
        <EmptyState
          icon={MusicNotesIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(recording.date)}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default HymnPage;
